const initialState = () => {
    return {
        isSearchComplete: false,
        isDeparturesComplete: false,
        isReturnsComplete: false,
        isPassengersComplete: false,
        isOptionsComplete: false,

        sessionId: null,
        searchParameters: null,
        departures: null,
        returns: null,
        departureTravel: null,
        returnTravel: null,
        passengers: null,
        travelOptionComments: null,
        travelOptions: null,
        totalCost: null,

        purchaser: {
            email: null,
            first_name: null,
            last_name: null,
            phone: null,
            age: null,
        },

        isInvoice: false,
        invoice: {
            email: null,
            company: null,
            vat_number: null,
            address: null,
            zip: null,
            city: null,
            country: null,
        },

        isCheckedCreateAccount: false,
        password: null,
        passwordConfirmation: null,

        ticketPrices: null,
        currency: 'pln',
    };
};

const state = initialState;

const getters = {
    getSearchParameters: (state) => {
        return state.searchParameters;
    },

    getDepartureResults: (state) => {
        return state.departures;
    },

    getReturnResults: (state) => {
        return state.returns;
    },

    getDepartureTravel: (state) => {
        return state.departureTravel;
    },

    getReturnTravel: (state) => {
        return state.returnTravel;
    },

    getPassengers: (state) => {
        return state.passengers;
    },

    getIsInvoice: (state) => {
        return state.isInvoice;
    },

    getPurchaser: (state) => {
        return state.purchaser;
    },

    isCheckedCreateAccount: (state) => {
        return state.isCheckedCreateAccount;
    },

    getInvoice: (state) => {
        return state.invoice;
    },

    getTravelOptionComments: (state) => {
        return state.travelOptionComments;
    },

    getTravelOptions: (state) => {
        return state.travelOptions;
    },

    getSessionID: (state) => {
        return state.sessionId;
    },

    getPassword: (state) => {
        return state.password;
    },

    getPasswordConfirmation: (state) => {
        return state.passwordConfirmation;
    },

    getTotalCost: (state) => {
        let cost = 0;
        if (!state.ticketPrices || !state.ticketPrices.normal) {
            cost = state.departureTravel.totalCost + (state.isReturnsComplete ? state.returnTravel.totalCost : 0);
            //add additional options costs
            if (state.travelOptions) {
                state.travelOptions.forEach((option) => {
                    cost += option.gross_price * option.quantity;
                });
            }
            return cost;
        }

        state.passengers.forEach((passenger) => {
            if (passenger.ticket_type === 'child') {
                cost += state.ticketPrices.children.value;
            } else {
                cost += state.ticketPrices.normal.value;
            }
        });

        return cost;
    },

    getIsSearchComplete: (state) => {
        return state.isSearchComplete;
    },

    getIsDeparturesComplete: (state) => {
        return state.isDeparturesComplete;
    },

    getIsReturnsComplete: (state) => {
        return state.isReturnsComplete;
    },

    getIsPassengersComplete: (state) => {
        return state.isPassengersComplete;
    },

    getIsOptionsComplete: (state) => {
        return state.isOptionsComplete;
    },

    getTicketPrices: (state) => {
        return state.ticketPrices;
    },

    getCurrency: (state) => {
        return state.currency;
    },
};

const mutations = {
    reset(state) {
        const initial = initialState();

        Object.keys(initial).forEach((key) => {
            state[key] = initial[key];
        });
    },

    setSearchParameters(state, parameters) {
        state.searchParameters = parameters;
    },

    setDeparturesResult(state, departures) {
        state.departures = departures;
    },

    setReturnsResult(state, returns) {
        state.returns = returns;
    },

    setDepartureTravel(state, departureTravel) {
        state.departureTravel = departureTravel;
    },

    setReturnTravel(state, returnTravel) {
        state.returnTravel = returnTravel;
    },

    setPassengers(state, passengers) {
        state.passengers = passengers;
    },

    setIsInvoice(state, bool) {
        state.isInvoice = bool;
    },

    setInvoice(state, invoice) {
        state.invoice = invoice;
    },

    setPurchaser(state, parameters) {
        state.purchaser = parameters;
    },

    setIsCheckedCreateAccount(state, value) {
        state.isCheckedCreateAccount = value;
    },

    setPassword(state, password) {
        state.password = password;
    },

    setPasswordConfirmation(state, password) {
        state.passwordConfirmation = password;
    },

    setTravelOptionComments(state, comments) {
        state.travelOptionComments = comments;
    },

    setTravelOptions(state, travelOptions) {
        state.travelOptions = travelOptions;
    },

    setSessionId(state, sessionId) {
        state.sessionId = sessionId;
    },

    setTotalCost(state, totalCost) {
        state.totalCost = totalCost;
    },

    setIsSearchComplete(state, value) {
        state.isSearchComplete = value;
    },

    setIsDeparturesComplete(state, value) {
        state.isDeparturesComplete = value;
    },

    setIsReturnsComplete(state, value) {
        state.isReturnsComplete = value;
    },

    setIsPassengersComplete(state, value) {
        state.isPassengersComplete = value;
    },

    setIsOptionsComplete(state, value) {
        state.isOptionsComplete = value;
    },

    setTicketPrices(state, value) {
        state.ticketPrices = value;
    },

    setCurrency(state, currencyCode) {
        state.currency = currencyCode;
    },
};

const actions = {
    reset({ commit }) {
        commit('reset');
    },

    setSearchParameters({ commit }, parameters) {
        commit('setSearchParameters', parameters);
    },

    setDeparturesResult({ commit }, departureResults) {
        commit('setDeparturesResult', departureResults);
    },

    setReturnsResult({ commit }, returnResults) {
        commit('setReturnsResult', returnResults);
    },

    setDepartureTravel({ commit }, departureTravel) {
        commit('setDepartureTravel', departureTravel);
    },

    setReturnTravel({ commit }, returnTravel) {
        commit('setReturnTravel', returnTravel);
    },

    increaseShownPassengersNumber({ commit }) {
        commit('increaseShownPassengersNumber');
    },

    setPassengers({ commit }, passengers) {
        commit('setPassengers', passengers);
    },

    setIsInvoice({ commit }, bool) {
        commit('setIsInvoice', bool);
    },

    setInvoice({ commit }, invoice) {
        commit('setInvoice', invoice);
    },

    setPurchaser({ commit }, parameters) {
        commit('setPurchaser', parameters);
    },

    setIsCheckedCreateAccount({ commit }, value) {
        commit('setIsCheckedCreateAccount', value);
    },

    setPassword({ commit }, password) {
        commit('setPassword', password);
    },

    setPasswordConfirmation({ commit }, password) {
        commit('setPasswordConfirmation', password);
    },

    setTravelOptionComments({commit}, comments) {
        commit('setTravelOptionComments', comments);
    },

    setTravelOptions({commit}, travelOptions) {
        commit('setTravelOptions', travelOptions);
    },

    setSessionId({commit}, sessionID) {
        commit('setSessionId', sessionID);
    },

    setTotalCost({commit}, totalCost) {
        commit('setTotalCost', totalCost);
    },

    setPassengersNumber({commit}, passengersNumber) {
        commit('setPassengersNumber', passengersNumber);
    },

    setIsSearchComplete({ commit }, value) {
        commit('setIsSearchComplete', value);
    },

    setIsDeparturesComplete({ commit }, value) {
        commit('setIsDeparturesComplete', value);
    },

    setIsReturnsComplete({ commit }, value) {
        commit('setIsReturnsComplete', value);
    },

    setIsPassengersComplete({ commit }, value) {
        commit('setIsPassengersComplete', value);
    },

    setIsOptionsComplete({ commit }, value) {
        commit('setIsOptionsComplete', value);
    },

    setTicketPrices({ commit }, value) {
        commit('setTicketPrices', value);
    },

    setCurrency({ commit} , value) {
        commit('setCurrency', value);
    },
};


export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
